/**
 * lex.ivoliq Honorarnoten-Verwaltung (Juli 2026).
 * Einstellungsmaske für die laufende Honorarnote (fix/variabel, Periode,
 * Abrechnungszeitraum, Direktversand), außerordentliche Honorarnote als
 * Entwurf mit PDF-Download, Liste aller Honorarnoten.
 * Styleguide: /app/design_guidelines_lex.json (Navy/Gold, Emerald = Status).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, PlusCircle, ListOrdered, Download, ChevronDown, Send, CircleCheck, Undo2, Ban, PencilLine } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { LexStornoDialog } from "@/components/lex/LexStornoDialog";
import { LexExtrasFields, LexSendDialog, emptyExtras, extrasPayload } from "@/components/lex/LexExtrasFields";
import { LexKammerBanner } from "@/components/lex/LexKammerBanner";
import "@/styles/lex.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) =>
  v == null ? "—" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};

const STATUS_BADGE = {
  created:   { label: "Entwurf",   style: { background: "#F8FAFC", color: "#64748B", border: "1px solid #CBD5E1" } },
  sent:      { label: "Versendet", style: { background: "#FBF7EF", color: "#8a6d33", border: "1px solid #C5A059" } },
  paid:      { label: "Bezahlt",   style: { background: "#ECFDF5", color: "#047857", border: "1px solid #059669" } },
  overdue:   { label: "Überfällig", style: { background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5" } },
  cancelled: { label: "Storniert", style: { background: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5" } },
};

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">{children}</label>
);

const CardHead = ({ icon: Icon, label }) => (
  <div className="flex items-center justify-between mb-6">
    <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</span>
    <Icon className="h-4 w-4" style={{ color: "#C5A059" }} />
  </div>
);

function CollapsibleCard({ icon: Icon, label, summary, testId, fadeClass, children, openSignal = 0 }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (openSignal) setOpen(true); }, [openSignal]);
  return (
    <div className={`lex-card ${fadeClass} self-start`} data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 p-6 text-left group"
        data-testid={`${testId}-toggle`} aria-expanded={open}>
        <span className="flex items-center gap-4 min-w-0">
          <Icon className="h-4 w-4 flex-shrink-0" style={{ color: "#C5A059" }} />
          <span className="min-w-0">
            <span className="block text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</span>
            <span className="block text-sm text-slate-600 mt-1 truncate">{summary}</span>
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-6 pt-5 border-t border-slate-100">{children}</div>}
    </div>
  );
}

const ToggleGroup = ({ options, value, onChange, testPrefix, activeStyle }) => (
  <div className="flex gap-2">
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testPrefix}-${v}`}
        className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border transition-colors ${
          value === v
            ? activeStyle?.[v] || "border-[#C5A059] text-[#0A1128] bg-[#FBF7EF]"
            : "border-slate-200 text-slate-400 hover:text-slate-600"
        }`}>{label}</button>
    ))}
  </div>
);

function SettingsCard({ template, onSaved, openSignal }) {
  const [form, setForm] = useState({
    mode: template.mode || "fix",
    amount: template.amount ?? "",
    anchor_day: template.anchor_day ?? 1,
    payment_term_days: template.payment_term_days ?? 14,
    status: template.status || "active",
    include_payment_qr: template.include_payment_qr ?? true,
    billing_period: template.billing_period || "previous",
    recurrence_interval: template.recurrence_interval || "monthly",
    auto_send: template.auto_send ?? false,
    paid_optout: template.paid_optout ?? false,
  });
  const [extrasDefault, setExtrasDefault] = useState(() => emptyExtras(
    Object.fromEntries(Object.entries(template.extras_default || {}).filter(([, v]) => v))));
  const [saving, setSaving] = useState(false);
  const brutto = form.amount ? Math.round(parseFloat(form.amount) * 1.2 * 100) / 100 : null;
  const isQuarterly = form.recurrence_interval === "quarterly";

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/lex/honorar`, {
        mode: form.mode,
        amount: parseFloat(form.amount),
        anchor_day: parseInt(form.anchor_day, 10),
        payment_term_days: parseInt(form.payment_term_days, 10),
        status: form.status,
        include_payment_qr: form.include_payment_qr,
        billing_period: form.billing_period,
        recurrence_interval: form.recurrence_interval,
        auto_send: form.mode === "fix" ? form.auto_send : false,
        paid_optout: form.paid_optout,
        extras_default: extrasPayload(extrasDefault),
      });
      toast.success("Einstellungen gespeichert.");
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const summary = `${template.mode === "variabel" ? "Variabel" : "Fix"} · ${fmtEur(template.amount)} netto / ${template.recurrence_interval === "quarterly" ? "Quartal" : "Monat"} · nächste Note ${fmtDate(template.next_run_date)}`;

  return (
    <CollapsibleCard icon={FileText} label="Laufende Honorarnote" summary={summary}
      testId="lex-honorar-settings-card" fadeClass="lex-fade-up-1" openSignal={openSignal}>
      <div className="space-y-4">
        <div>
          <Label>Empfänger-Kanzlei</Label>
          <p className="lex-mono text-sm text-slate-700 border-b border-slate-200 pb-2" data-testid="lex-honorar-kanzlei">
            {template.customer_name || "—"} <span className="text-slate-400">· aus dem Onboarding</span>
          </p>
        </div>
        <div>
          <Label>Modus</Label>
          <ToggleGroup options={[["fix", "Fixes Honorar"], ["variabel", "Variabel"]]}
            value={form.mode} onChange={(m) => setForm({ ...form, mode: m })} testPrefix="lex-honorar-mode" />
          {form.mode === "variabel" ? (
            <p className="text-xs text-slate-400 mt-2" data-testid="lex-honorar-variabel-hint">
              Der Betrag gilt als Richtwert: Jede Note wird zuerst als Entwurf erstellt und Sie bestätigen den
              Nettobetrag per Mail — <span className="text-slate-600 font-medium">der Betrag ist dabei anpassbar</span>,
              bevor die Note an die Kanzlei geht.
            </p>
          ) : (
            <label className="flex items-start gap-3 cursor-pointer mt-3" data-testid="lex-honorar-autosend-label">
              <input type="checkbox" className="mt-0.5 accent-[#C5A059]" checked={form.auto_send}
                onChange={(e) => setForm({ ...form, auto_send: e.target.checked })}
                data-testid="lex-honorar-autosend-checkbox" />
              <span className="text-sm text-slate-700">
                <span className="font-semibold">Direktversand an die Kanzlei</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Aktiviert: Die Note geht am Erstellungstag automatisch per Mail an die Kanzlei.
                  Deaktiviert: Sie geben jede Note zuerst mit einem Klick frei.
                </span>
              </span>
            </label>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{isQuarterly ? "Nettobetrag / Quartal" : "Nettobetrag / Monat"}</Label>
            <input type="number" min="1" step="0.01" className="lex-input" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="lex-honorar-amount" />
            <p className="text-xs text-slate-400 mt-1">= {fmtEur(brutto)} brutto (20 % USt)</p>
          </div>
          <div>
            <Label>Erstellungstag im Monat</Label>
            <input type="number" min="1" max="31" className="lex-input" value={form.anchor_day}
              onChange={(e) => setForm({ ...form, anchor_day: e.target.value })} data-testid="lex-honorar-anchor-day" />
            <p className="text-xs text-slate-400 mt-1">1–31 · 29.–31. fällt automatisch auf den Monatsletzten</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Abgerechnet wird</Label>
            <ToggleGroup options={[["previous", isQuarterly ? "Vorquartal" : "Vormonat"], ["current", "Laufende Periode"]]}
              value={form.billing_period} onChange={(v) => setForm({ ...form, billing_period: v })}
              testPrefix="lex-honorar-period" />
          </div>
          <div>
            <Label>Periode</Label>
            <ToggleGroup options={[["monthly", "Monatlich"], ["quarterly", "Quartal"]]}
              value={form.recurrence_interval} onChange={(v) => setForm({ ...form, recurrence_interval: v })}
              testPrefix="lex-honorar-interval" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 items-end">
          <div>
            <Label>Zahlungsziel (Tage)</Label>
            <input type="number" min="0" max="90" className="lex-input" value={form.payment_term_days}
              onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} data-testid="lex-honorar-payment-term" />
          </div>
          <div>
            <Label>Status</Label>
            <ToggleGroup options={[["active", "Aktiv"], ["paused", "Pausiert"]]}
              value={form.status} onChange={(s) => setForm({ ...form, status: s })} testPrefix="lex-honorar-status"
              activeStyle={{
                active: "border-[#059669] text-[#047857] bg-[#ECFDF5]",
                paused: "border-slate-400 text-slate-600 bg-slate-50",
              }} />
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="lex-honorar-qr-label">
            <input type="checkbox" className="mt-0.5 accent-[#C5A059]" checked={form.include_payment_qr}
              onChange={(e) => setForm({ ...form, include_payment_qr: e.target.checked })}
              data-testid="lex-honorar-qr-checkbox" />
            <span className="text-sm text-slate-700">
              <span className="font-semibold">QR-Zahlung auf der Note</span>
              <span className="block text-xs text-slate-400 mt-0.5">
                EPC-QR-Code (GiroCode) zum direkten Überweisen · gilt für laufende und Sonder-Noten.
              </span>
            </span>
          </label>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="lex-honorar-optout-label">
            <input type="checkbox" className="mt-0.5 accent-[#C5A059]" checked={form.paid_optout}
              onChange={(e) => setForm({ ...form, paid_optout: e.target.checked })}
              data-testid="lex-honorar-optout-checkbox" />
            <span className="text-sm text-slate-700">
              <span className="font-semibold">Meine Kanzlei zahlt verlässlich im Rechnungsmonat</span>
              <span className="block text-xs text-slate-400 mt-0.5">
                Noten werden zum Monatsende automatisch als bezahlt verbucht — wichtig für die
                Ist-Besteuerung, weil das Zahlungsdatum bestimmt, in welche UVA-Periode ein Honorar fällt.
                Nur aktivieren, wenn Zahlungen sicher nie in den Folgemonat rutschen.
              </span>
            </span>
          </label>
        </div>
        <div className="pt-4 mt-4 border-t border-slate-100" data-testid="lex-honorar-extras-default">
          <LexExtrasFields value={extrasDefault} onChange={setExtrasDefault} testPrefix="lex-honorar-extras-default" />
          <p className="text-xs text-slate-400 mt-2">
            Vorlage für regelmäßige Posten · wird bei jeder Prüfung einer laufenden Note vorbefüllt und ist dort änderbar.
            Bei Direktversand ohne Prüfung wird nichts ergänzt.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-4">
        <p className="text-xs text-slate-400">
          Nächste Note: <span className="lex-mono text-slate-600">{fmtDate(template.next_run_date)}</span>
        </p>
        <button onClick={save} disabled={saving} className="lex-btn !px-6" data-testid="lex-honorar-save-btn">
          {saving ? "Speichert …" : "Speichern"}
        </button>
      </div>
    </CollapsibleCard>
  );
}

function ExtraCard({ template, onCreated }) {
  const emptyForm = {
    description: "", amount: "", service_date: "", service_date_end: "",
    payment_term_days: template?.payment_term_days ?? 14,
  };
  const [form, setForm] = useState(emptyForm);
  const [extras, setExtras] = useState(emptyExtras());
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/lex/honorar/extra`, {
        description: form.description,
        amount: parseFloat(form.amount),
        service_date: form.service_date,
        service_date_end: form.service_date_end || null,
        payment_term_days: parseInt(form.payment_term_days, 10),
        extras: extrasPayload(extras),
      });
      toast.success(`Entwurf ${res.data.invoice_number} erstellt · PDF & Versand in der Übersicht unten.`);
      setForm(emptyForm);
      setExtras(emptyExtras());
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erstellung fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsibleCard icon={PlusCircle} label="Außerordentliche Honorarnote"
      summary="Sonder-Note für Substitution & Projekte · Entwurf mit PDF-Download"
      testId="lex-honorar-extra-card" fadeClass="lex-fade-up-2">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-4">
          <div>
            <Label>Empfänger-Kanzlei</Label>
            <p className="lex-mono text-sm text-slate-700 border-b border-slate-200 pb-2" data-testid="lex-extra-kanzlei">
              {template?.customer_name || "—"} <span className="text-slate-400">· aus dem Onboarding</span>
            </p>
          </div>
          <div>
            <Label>Leistungsbeschreibung *</Label>
            <input className="lex-input" value={form.description} required
              placeholder="z. B. Substitution HV 15.07.2026, BG Innere Stadt"
              onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="lex-extra-description" />
            <p className="text-xs text-slate-400 mt-1">Für Sonderleistungen außerhalb des laufenden Honorars (z. B. Substitution, Projekt)</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Leistungsdatum *</Label>
              <input type="date" className="lex-input" value={form.service_date} required
                onChange={(e) => setForm({ ...form, service_date: e.target.value })} data-testid="lex-extra-service-date" />
            </div>
            <div>
              <Label>Zeitraum bis (optional)</Label>
              <input type="date" className="lex-input" value={form.service_date_end} min={form.service_date || undefined}
                onChange={(e) => setForm({ ...form, service_date_end: e.target.value })} data-testid="lex-extra-service-date-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nettobetrag *</Label>
              <input type="number" min="0.01" step="0.01" className="lex-input" value={form.amount} required
                onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="lex-extra-amount" />
              <p className="text-xs text-slate-400 mt-1">20 % USt wird automatisch ergänzt</p>
            </div>
            <div>
              <Label>Zahlungsziel (Tage)</Label>
              <input type="number" min="0" max="90" className="lex-input" value={form.payment_term_days}
                onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} data-testid="lex-extra-payment-term" />
            </div>
          </div>
          <LexExtrasFields value={extras} onChange={setExtras} testPrefix="lex-extra-extras" />
          </div>
        <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-4">
          <p className="text-xs text-slate-400">
            Entwurf · Versand nach Ihrer Freigabe direkt aus der Applikation
          </p>
          <button type="submit" disabled={saving} className="lex-btn !px-6" data-testid="lex-extra-submit-btn">
            {saving ? "Speichert …" : "Als Entwurf speichern"}
          </button>
        </div>
      </form>
    </CollapsibleCard>
  );
}

function InvoiceList({ invoices, onChanged, extrasDefault }) {
  const [busy, setBusy] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [paidId, setPaidId] = useState(null);
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [stornoAction, setStornoAction] = useState(null);

  const downloadPdf = async (inv) => {
    setBusy(`pdf-${inv.id}`);
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Honorarnote_${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF-Download fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const send = async (inv, extras) => {
    setBusy(`send-${inv.id}`);
    try {
      const res = await axios.post(`${API}/lex/honorar/${inv.id}/send`, { extras });
      toast.success(`Note ${res.data.invoice_number} an ${res.data.recipient} versendet · Bestätigung mit PDF folgt per Mail.`);
      // Conversion-Event: erste versendete Honorarnote = kein anderer Eintrag war bereits sent/paid/overdue
      const isFirst = !invoices.some((x) => x.id !== inv.id && ["sent", "paid", "overdue"].includes(x.status));
      trackEvent(isFirst ? "lex_first_invoice_sent" : "lex_invoice_sent", { extra: Boolean(inv.lex_extra) });
      setConfirmId(null);
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Versand fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  // Bezahlt-Markierung mit Datum (Juli 2026) — Übergangslösung bis PSD2-Bankanbindung.
  const markPaid = async (inv) => {
    setBusy(`paid-${inv.id}`);
    try {
      await axios.put(`${API}/invoices/${inv.id}`, {
        status: "paid",
        paid_at: new Date(`${paidDate}T12:00:00Z`).toISOString(),
      });
      toast.success(`Note ${inv.invoice_number} als bezahlt markiert (${new Date(paidDate).toLocaleDateString("de-AT")}).`);
      setPaidId(null);
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Konnte nicht als bezahlt markiert werden.");
    } finally {
      setBusy(null);
    }
  };

  const unmarkPaid = async (inv) => {
    setBusy(`paid-${inv.id}`);
    try {
      await axios.put(`${API}/invoices/${inv.id}`, { status: "sent" });
      toast.success(`Note ${inv.invoice_number} wieder auf „Versendet" gesetzt.`);
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Änderung fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="lex-card p-8 lex-fade-up-3" data-testid="lex-honorar-list-card">
      <CardHead icon={ListOrdered} label="Bisherige Honorarnoten" />
      {invoices.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="lex-honorar-list-empty">
          Noch keine Honorarnoten erstellt. Die erste Note entsteht automatisch zum nächsten Erstellungstag.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lex-honorar-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3 font-semibold">Nummer</th>
                <th className="py-2 pr-3 font-semibold">Datum</th>
                <th className="py-2 pr-3 font-semibold">Zahlbar bis</th>
                <th className="py-2 pr-3 font-semibold">Typ</th>
                <th className="py-2 pr-3 font-semibold text-right">Betrag</th>
                <th className="py-2 pr-3 font-semibold text-right">Status</th>
                <th className="py-2 font-semibold text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.created;
                const isDraft = inv.status === "created" || inv.status === "draft";
                const overdue = inv.due_date && !["paid", "cancelled"].includes(inv.status) && !isDraft
                  && new Date(inv.due_date) < new Date(new Date().toDateString());
                return (
                  <tr key={inv.id} className="border-b border-slate-100">
                    <td className="py-2.5 pr-3 lex-mono text-slate-800">{inv.invoice_number}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{fmtDate(inv.invoice_date)}</td>
                    <td className="py-2.5 pr-3" data-testid={`lex-inv-due-${inv.invoice_number}`}>
                      <span className={overdue ? "font-semibold" : "text-slate-600"} style={overdue ? { color: "#B91C1C" } : {}}>
                        {fmtDate(inv.due_date)}
                      </span>
                      {overdue && (
                        <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#B91C1C" }}
                          data-testid={`lex-inv-overdue-${inv.invoice_number}`}>
                          Überfällig
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-500" data-testid={`lex-inv-type-${inv.invoice_number}`}>
                      {inv.invoice_type === "storno" ? "Storno" : inv.invoice_type === "correction" ? "Korrektur"
                        : inv.lex_extra ? "Außerordentlich" : "Laufend"}
                    </td>
                    <td className="py-2.5 pr-3 lex-mono text-right text-slate-800">{fmtEur(inv.total)}</td>
                    <td className="py-2.5 pr-3 text-right">
                      <span className="text-[11px] px-2 py-1 uppercase tracking-wider font-semibold" style={badge.style}>
                        {badge.label}
                      </span>
                      {inv.status === "paid" && inv.paid_at && (
                        <div className="text-[10px] text-slate-400 mt-1 lex-mono" data-testid={`lex-inv-paid-date-${inv.invoice_number}`}>
                          {fmtDate(inv.paid_at)}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <button type="button" onClick={() => downloadPdf(inv)} disabled={busy === `pdf-${inv.id}`}
                          title="PDF herunterladen"
                          className="p-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors disabled:opacity-50"
                          data-testid={`lex-inv-pdf-${inv.invoice_number}`}>
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        {isDraft && (
                          <button type="button" onClick={() => setConfirmId(inv.id)}
                            title="An die Kanzlei senden"
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                            data-testid={`lex-inv-send-${inv.invoice_number}`}>
                            <Send className="h-3 w-3" /> Senden
                          </button>
                        )}
                        {(inv.status === "sent" || inv.status === "overdue") && (paidId === inv.id ? (
                          <span className="inline-flex items-center gap-1.5">
                            <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)}
                              className="text-[11px] border border-slate-200 px-1.5 py-1 lex-mono text-slate-700 focus:outline-none focus:border-[#C5A059]"
                              data-testid={`lex-inv-paid-date-input-${inv.invoice_number}`} />
                            <button type="button" onClick={() => markPaid(inv)} disabled={busy === `paid-${inv.id}` || !paidDate}
                              className="text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1.5 transition-colors disabled:opacity-50"
                              style={{ background: "#C5A059", color: "#0A1128" }}
                              data-testid={`lex-inv-paid-confirm-${inv.invoice_number}`}>
                              {busy === `paid-${inv.id}` ? "Speichert …" : "OK"}
                            </button>
                            <button type="button" onClick={() => setPaidId(null)}
                              className="text-[11px] text-slate-400 hover:text-slate-600 px-1"
                              data-testid={`lex-inv-paid-cancel-${inv.invoice_number}`}>✕</button>
                          </span>
                        ) : (
                          <button type="button" onClick={() => { setPaidId(inv.id); setPaidDate(new Date().toISOString().slice(0, 10)); }}
                            title="Als bezahlt markieren"
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1.5 border border-slate-200 text-slate-500 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                            data-testid={`lex-inv-paid-${inv.invoice_number}`}>
                            <CircleCheck className="h-3 w-3" /> Bezahlt
                          </button>
                        ))}
                        {inv.status === "paid" && (
                          <button type="button" onClick={() => unmarkPaid(inv)} disabled={busy === `paid-${inv.id}`}
                            title="Zahlung zurücksetzen (Status: Versendet)"
                            className="p-1.5 border border-slate-200 text-slate-400 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors disabled:opacity-50"
                            data-testid={`lex-inv-unpaid-${inv.invoice_number}`}>
                            <Undo2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canStorno(inv) && (
                          <>
                            <button type="button" onClick={() => setStornoAction({ inv, mode: "korrektur" })}
                              title="Korrigieren (Storno + neue Note)"
                              className="p-1.5 border border-slate-200 text-slate-400 hover:text-[#0A1128] hover:border-[#C5A059] transition-colors"
                              data-testid={`lex-inv-korrektur-${inv.invoice_number}`}>
                              <PencilLine className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={() => setStornoAction({ inv, mode: "storno" })}
                              title="Stornieren"
                              className="p-1.5 border border-slate-200 text-slate-400 hover:text-[#B91C1C] hover:border-[#FCA5A5] transition-colors"
                              data-testid={`lex-inv-storno-${inv.invoice_number}`}>
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {confirmId && (() => {
        const inv = invoices.find((x) => x.id === confirmId);
        return inv ? <LexSendDialog invoice={inv} defaults={extrasDefault} busy={busy === `send-${inv.id}`}
          onClose={() => setConfirmId(null)} onSend={(extras) => send(inv, extras)} /> : null;
      })()}
      {stornoAction && (
        <LexStornoDialog invoice={stornoAction.inv} mode={stornoAction.mode}
          onClose={() => setStornoAction(null)}
          onDone={() => { setStornoAction(null); onChanged(); }} />
      )}
    </div>
  );
}

const canStorno = (inv) => (!inv.invoice_type || inv.invoice_type === "invoice")
  && !inv.cancelled_by_invoice_id && ["sent", "paid", "overdue"].includes(inv.status);

export default function LexHonorar() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openSignal, setOpenSignal] = useState(0);
  const [loadCount, setLoadCount] = useState(0);

  const load = useCallback(() => {
    axios.get(`${API}/lex/honorar`)
      .then((res) => { setData(res.data); setLoadCount((c) => c + 1); })
      .catch((err) => setError(err?.response?.data?.detail || "Laden fehlgeschlagen."));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return <div className="lex-theme py-10 text-center text-slate-500" data-testid="lex-honorar-error">{String(error)}</div>;
  }
  if (!data) {
    return <div className="lex-theme py-10 text-center text-slate-400" data-testid="lex-honorar-loading">Lädt …</div>;
  }

  return (
    <div className="lex-theme" data-testid="lex-honorar-page">
      <div className="mb-10 lex-fade-up">
        <div className="lex-gold-line mb-4" />
        <h1 className="lex-serif text-4xl sm:text-5xl font-semibold" style={{ color: "#0A1128" }}>Honorarnoten.</h1>
        <p className="text-sm text-slate-500 mt-2">
          Ihre laufende Note entsteht automatisch · hier passen Sie die Eckdaten an und erstellen Sonder-Noten.
        </p>
      </div>
      <LexKammerBanner refreshKey={loadCount} onReview={() => setOpenSignal((n) => n + 1)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
        <SettingsCard template={data.template} onSaved={load} openSignal={openSignal} />
        <ExtraCard template={data.template} onCreated={load} />
      </div>
      <InvoiceList invoices={data.invoices || []} onChanged={load} extrasDefault={data.template?.extras_default || {}} />
    </div>
  );
}
